import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '@/store';
import { adminApi, businessModelsApi, categoriesApi, shopsApi } from '@/services/api';
import Button from '@/components/ui/Button';
import BackButton from '@/components/ui/BackButton';
import { BanknotesIcon, ExclamationTriangleIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface CommissionRule {
  id: number;
  business_model_id?: number;
  policy_enabled?: boolean;
  rule_type: string;
  shop_type?: string | null;
  category_id?: number | null;
  shop_id?: number | null;
  product_type?: string | null;
  min_price?: number | null;
  max_price?: number | null;
  commission_percentage: number;
  min_commission?: number | null;
  max_commission?: number | null;
  prepaid_commission?: number | null;
  cod_commission?: number | null;
  priority?: number;
  rule_name?: string | null;
  description?: string | null;
  is_active: boolean;
}

interface Category {
  id: number;
  name: string;
}

interface Shop {
  id: number;
  name: string;
}

const CommissionSettingsPage: React.FC = () => {
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<CommissionRule | null>(null);
  
  const [commissionRules, setCommissionRules] = useState<CommissionRule[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [defaultBusinessModelId, setDefaultBusinessModelId] = useState<number | null>(null);
  const [newRule, setNewRule] = useState({
    policy_enabled: true,
    rule_type: 'global',
    shop_type: '',
    category_id: null as number | null,
    shop_id: null as number | null,
    product_type: '',
    min_price: null as number | null,
    max_price: null as number | null,
    commission_percentage: 5,
    min_commission: null as number | null,
    max_commission: null as number | null,
    prepaid_commission: null as number | null,
    cod_commission: null as number | null,
    priority: 0,
    rule_name: '',
    description: '',
    is_active: true,
  });

  // Get current role from URL
  const urlParams = new URLSearchParams(location.search);
  const currentRole = urlParams.get('role') || 'super_admin';

  useEffect(() => {
    fetchDefaultBusinessModel();
    fetchCommissionRules();
    fetchCategories();
    fetchShops();
  }, []);

  const fetchDefaultBusinessModel = async () => {
    try {
      const response = await businessModelsApi.getAll();
      const defaultModel = response.data.find((model: any) => model.is_default && model.is_active);
      if (defaultModel) {
        setDefaultBusinessModelId(defaultModel.id);
      } else if (response.data.length > 0) {
        const activeModel = response.data.find((model: any) => model.is_active);
        if (activeModel) {
          setDefaultBusinessModelId(activeModel.id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch default business model:', err);
      setDefaultBusinessModelId(1);
    }
  };

  const fetchCommissionRules = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await businessModelsApi.getCommissionRules(defaultBusinessModelId);
      setCommissionRules(response.data || []);
    } catch (err: any) {
      let errorMessage = 'Failed to load commission rules';
      if (err?.detail) {
        errorMessage = typeof err.detail === 'string' ? err.detail : String(err.detail);
      }
      setError(errorMessage);
      console.error('Commission rules fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await shopsApi.getAll();
      setShops(response.data || []);
    } catch (err) {
      console.error('Failed to fetch shops:', err);
    }
  };

  const handleSaveRule = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      if (!defaultBusinessModelId) {
        toast.error('No business model available. Please create a business model first.');
        setIsSaving(false);
        return;
      }
      
      const ruleData: any = {
        business_model_id: defaultBusinessModelId,
        policy_enabled: newRule.policy_enabled,
        rule_type: newRule.rule_type,
        commission_percentage: newRule.commission_percentage,
        priority: newRule.priority,
        rule_name: newRule.rule_name || null,
        description: newRule.description || null,
        is_active: newRule.is_active,
      };

      // Add optional fields only if they have values
      if (newRule.shop_type) ruleData.shop_type = newRule.shop_type;
      if (newRule.category_id) ruleData.category_id = newRule.category_id;
      if (newRule.shop_id) ruleData.shop_id = newRule.shop_id;
      if (newRule.product_type) ruleData.product_type = newRule.product_type;
      if (newRule.min_price !== null) ruleData.min_price = newRule.min_price;
      if (newRule.max_price !== null) ruleData.max_price = newRule.max_price;
      if (newRule.min_commission !== null) ruleData.min_commission = newRule.min_commission;
      if (newRule.max_commission !== null) ruleData.max_commission = newRule.max_commission;
      if (newRule.prepaid_commission !== null) ruleData.prepaid_commission = newRule.prepaid_commission;
      if (newRule.cod_commission !== null) ruleData.cod_commission = newRule.cod_commission;
      
      if (editingRule) {
        await businessModelsApi.updateCommissionRule(editingRule.id, ruleData);
        toast.success('Commission rule updated successfully');
        setEditingRule(null);
      } else {
        await businessModelsApi.createCommissionRule(ruleData);
        toast.success('Commission rule created successfully');
      }
      
      resetForm();
      fetchCommissionRules();
    } catch (err: any) {
      let errorMessage = 'Failed to save commission rule';
      if (err?.detail) {
        errorMessage = typeof err.detail === 'string' ? err.detail : String(err.detail);
      }
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setNewRule({
      policy_enabled: true,
      rule_type: 'global',
      shop_type: '',
      category_id: null,
      shop_id: null,
      product_type: '',
      min_price: null,
      max_price: null,
      commission_percentage: 5,
      min_commission: null,
      max_commission: null,
      prepaid_commission: null,
      cod_commission: null,
      priority: 0,
      rule_name: '',
      description: '',
      is_active: true,
    });
  };

  const handleEdit = (rule: CommissionRule) => {
    setEditingRule(rule);
    setNewRule({
      policy_enabled: rule.policy_enabled ?? true,
      rule_type: rule.rule_type,
      shop_type: rule.shop_type || '',
      category_id: rule.category_id || null,
      shop_id: rule.shop_id || null,
      product_type: rule.product_type || '',
      min_price: rule.min_price || null,
      max_price: rule.max_price || null,
      commission_percentage: rule.commission_percentage,
      min_commission: rule.min_commission || null,
      max_commission: rule.max_commission || null,
      prepaid_commission: rule.prepaid_commission || null,
      cod_commission: rule.cod_commission || null,
      priority: rule.priority || 0,
      rule_name: rule.rule_name || '',
      description: rule.description || '',
      is_active: rule.is_active,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (ruleId: number) => {
    if (!window.confirm('Are you sure you want to delete this commission rule?')) {
      return;
    }
    
    try {
      await businessModelsApi.deleteCommissionRule(ruleId);
      toast.success('Commission rule deleted successfully');
      fetchCommissionRules();
    } catch (err: any) {
      toast.error('Failed to delete commission rule');
    }
  };

  const getRuleDescription = (rule: CommissionRule): string => {
    const parts: string[] = [];
    if (rule.rule_name) parts.push(rule.rule_name);
    if (rule.shop_id) parts.push(`Shop: ${shops.find(s => s.id === rule.shop_id)?.name || rule.shop_id}`);
    if (rule.category_id) parts.push(`Category: ${categories.find(c => c.id === rule.category_id)?.name || rule.category_id}`);
    if (rule.shop_type) parts.push(`Shop Type: ${rule.shop_type}`);
    if (rule.product_type) parts.push(`Product Type: ${rule.product_type}`);
    if (rule.min_price || rule.max_price) {
      const min = rule.min_price ? `$${rule.min_price}` : '$0';
      const max = rule.max_price ? `$${rule.max_price}` : '∞';
      parts.push(`Price Range: ${min} - ${max}`);
    }
    return parts.length > 0 ? parts.join(' | ') : rule.rule_type;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading commission settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <BackButton to={`/dashboard?role=${currentRole}`} />
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <BanknotesIcon className="h-8 w-8 text-gray-500 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Commission Policy Management</h1>
              <p className="text-gray-600 mt-2">Configure flexible commission rules with multiple filter options</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        {/* Add/Edit Rule Form */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {editingRule ? 'Edit Commission Rule' : 'Create Commission Rule'}
          </h2>
          
          <div className="space-y-6">
            {/* Policy Enabled Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="policy_enabled"
                checked={newRule.policy_enabled}
                onChange={(e) => setNewRule(prev => ({ ...prev, policy_enabled: e.target.checked }))}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="policy_enabled" className="ml-2 text-sm font-medium text-gray-700">
                Policy Enabled (Commission payment required)
              </label>
            </div>

            {/* Rule Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rule Name (Optional)
              </label>
              <input
                type="text"
                value={newRule.rule_name}
                onChange={(e) => setNewRule(prev => ({ ...prev, rule_name: e.target.value }))}
                placeholder="e.g., Recycled Products 5% Commission"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Rule Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rule Type
              </label>
              <select
                value={newRule.rule_type}
                onChange={(e) => setNewRule(prev => ({ ...prev, rule_type: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="global">Global (All Products)</option>
                <option value="shop_type">Shop Type</option>
                <option value="category">Category</option>
                <option value="shop_specific">Shop Specific</option>
                <option value="product_type">Product Type</option>
                <option value="price_range">Price Range</option>
                <option value="combined">Combined (Multiple Filters)</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Shop Type */}
              {(newRule.rule_type === 'shop_type' || newRule.rule_type === 'combined') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shop Type
                  </label>
                  <select
                    value={newRule.shop_type}
                    onChange={(e) => setNewRule(prev => ({ ...prev, shop_type: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Shop Types</option>
                    <option value="normal">Normal</option>
                    <option value="business">Business</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
              )}

              {/* Category */}
              {(newRule.rule_type === 'category' || newRule.rule_type === 'combined') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={newRule.category_id || ''}
                    onChange={(e) => setNewRule(prev => ({ ...prev, category_id: e.target.value ? parseInt(e.target.value) : null }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Shop Specific */}
              {(newRule.rule_type === 'shop_specific' || newRule.rule_type === 'combined') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shop
                  </label>
                  <select
                    value={newRule.shop_id || ''}
                    onChange={(e) => setNewRule(prev => ({ ...prev, shop_id: e.target.value ? parseInt(e.target.value) : null }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Shops</option>
                    {shops.map(shop => (
                      <option key={shop.id} value={shop.id}>{shop.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Product Type */}
              {(newRule.rule_type === 'product_type' || newRule.rule_type === 'combined') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Type
                  </label>
                  <select
                    value={newRule.product_type}
                    onChange={(e) => setNewRule(prev => ({ ...prev, product_type: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Product Types</option>
                    <option value="recycled">Recycled</option>
                    <option value="brand_new">Brand New</option>
                  </select>
                </div>
              )}

              {/* Price Range */}
              {(newRule.rule_type === 'price_range' || newRule.rule_type === 'combined') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Price ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newRule.min_price || ''}
                      onChange={(e) => setNewRule(prev => ({ ...prev, min_price: e.target.value ? parseFloat(e.target.value) : null }))}
                      placeholder="0.00"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Maximum Price ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newRule.max_price || ''}
                      onChange={(e) => setNewRule(prev => ({ ...prev, max_price: e.target.value ? parseFloat(e.target.value) : null }))}
                      placeholder="No limit"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </>
              )}

              {/* Commission Percentage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commission Percentage (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={newRule.commission_percentage}
                  onChange={(e) => setNewRule(prev => ({ ...prev, commission_percentage: parseFloat(e.target.value) || 0 }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority (Higher = More Specific)
                </label>
                <input
                  type="number"
                  min="0"
                  value={newRule.priority}
                  onChange={(e) => setNewRule(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Prepaid Commission */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prepaid Commission (%) (Optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={newRule.prepaid_commission || ''}
                  onChange={(e) => setNewRule(prev => ({ ...prev, prepaid_commission: e.target.value ? parseFloat(e.target.value) : null }))}
                  placeholder="Same as default"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* COD Commission */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  COD Commission (%) (Optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={newRule.cod_commission || ''}
                  onChange={(e) => setNewRule(prev => ({ ...prev, cod_commission: e.target.value ? parseFloat(e.target.value) : null }))}
                  placeholder="Same as default"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Min Commission */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Commission ($) (Optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newRule.min_commission || ''}
                  onChange={(e) => setNewRule(prev => ({ ...prev, min_commission: e.target.value ? parseFloat(e.target.value) : null }))}
                  placeholder="No minimum"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Max Commission */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Commission ($) (Optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newRule.max_commission || ''}
                  onChange={(e) => setNewRule(prev => ({ ...prev, max_commission: e.target.value ? parseFloat(e.target.value) : null }))}
                  placeholder="No maximum"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                value={newRule.description}
                onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this rule applies to..."
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={newRule.is_active}
                onChange={(e) => setNewRule(prev => ({ ...prev, is_active: e.target.checked }))}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700">
                Rule is Active
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            {editingRule && (
              <Button
                variant="outline"
                onClick={() => {
                  setEditingRule(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={handleSaveRule}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </div>
        </div>

        {/* Existing Rules */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Commission Rules</h2>
          
          {commissionRules.length === 0 ? (
            <div className="text-center py-8">
              <BanknotesIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No commission rules configured</p>
            </div>
          ) : (
            <div className="space-y-4">
              {commissionRules.map((rule) => (
                <div key={rule.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-gray-900">
                          {rule.rule_name || getRuleDescription(rule)}
                        </h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          rule.policy_enabled 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {rule.policy_enabled ? 'Policy Enabled' : 'Policy Disabled'}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          rule.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {rule.description && (
                        <p className="text-sm text-gray-600 mb-2">{rule.description}</p>
                      )}
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          <span className="font-medium">Rate:</span> {rule.commission_percentage}%
                          {rule.prepaid_commission && ` (Prepaid: ${rule.prepaid_commission}%)`}
                          {rule.cod_commission && ` (COD: ${rule.cod_commission}%)`}
                        </p>
                        {(rule.min_commission || rule.max_commission) && (
                          <p>
                            <span className="font-medium">Limits:</span> 
                            Min: ${rule.min_commission || 0} | 
                            Max: ${rule.max_commission || 'Unlimited'}
                          </p>
                        )}
                        <p>
                          <span className="font-medium">Priority:</span> {rule.priority || 0}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleEdit(rule)}
                        className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded"
                        title="Edit rule"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(rule.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded"
                        title="Delete rule"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommissionSettingsPage;
